import { BaseSocketService, SocketServiceConfig } from '../index';

// ----------- Type Definitions -----------
export interface MiaChatV2Event {
    conversation_id: string;
    message?: {
        role?: 'user' | 'assistant' | 'system';
        content?: string;
        processing_type?: string; // Backend sends processing_type in message
        timestamp?: string;
        sections?: Array<{
            index: number;
            type: string;
            main?: string;
            sub_text?: string;
        }>;
        text?: {
            key: {
                index: number;
                type: string;
                main?: string;
                sub_text?: string;
            };
            value: string;
        };
    };
    email_content?: {
        subject: string;
        sections: Array<{
            type: string;
            content: string;
            order: number;
        }>;
    };
    suggestions?: Array<{
        id: string;
        text: string;
        type?: string;
    }>;
    next_action?: string;
    is_input_restricted_to_suggestions?: boolean;
    processing_type?: 'processing' | 'generating' | 'completed' | 'error';
    status?: 'success' | 'error' | 'processing' | 'completed';
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    // Tool call specific properties (for backward compatibility)
    tool_name?: string;
    tool_parameters?: any;
    tool_result?: any;
    execution_time?: number;
    timestamp: string;
}

export interface MiaChatV2Subscription {
    conversation_id: string;
    callback: (data: MiaChatV2Event) => void;
    active: boolean;
}

export interface SubscribeConversationRequest {
    conversation_id: string;
    token?: string;
}

export interface UnsubscribeConversationRequest {
    conversation_id: string;
}

// ----------- MiaChatV2 Socket Service -----------
class MiaChatV2SocketService extends BaseSocketService {
    private static instance: MiaChatV2SocketService;
    private subscriptions: Map<string, MiaChatV2Subscription> = new Map();
    private eventCache: Map<string, MiaChatV2Event> = new Map();

    constructor(config: SocketServiceConfig) {
        super(config);
    }

    // Singleton pattern
    static getInstance(config?: SocketServiceConfig): MiaChatV2SocketService {
        if (!MiaChatV2SocketService.instance) {
            if (!config) {
                throw new Error('MiaChatV2SocketService requires config for first initialization');
            }
            MiaChatV2SocketService.instance = new MiaChatV2SocketService(config);
        }
        return MiaChatV2SocketService.instance;
    }

    // Service name
    getServiceName(): string {
        return 'MiaChatV2SocketService';
    }

    // Events this service handles (matching MCPContext events)
    getEvents(): string[] {
        return [
            'token_usage',           // Token usage updates
            'agent_message',         // Agent message updates
            'processing_status',     // Processing status updates
            'streaming_section_response',    // Streaming response updates
            'conversation_update',   // General conversation updates
            'email_generation',      // Email generation events
            'suggestion_update',     // Suggestion updates
            'executing_tool_call',   // Tool call execution started
            'tool_call_completed',   // Tool call execution completed
            'subscribe_success',     // Subscription confirmation
            'subscribe_error',       // Subscription error
            'unsubscribe_success',   // Unsubscription confirmation
            'unsubscribe_error',     // Unsubscription error
        ];
    }

    // Subscribe to conversation events
    subscribeToConversation(
        conversationId: string, 
        callback: (data: MiaChatV2Event) => void
    ): boolean {
        if (!conversationId || typeof callback !== 'function') {
            console.error('❌ Invalid conversation ID or callback for subscription');
            return false;
        }

        // Check if already subscribed
        if (this.subscriptions.has(conversationId)) {
            console.log(`📡 Already subscribed to conversation: ${conversationId}`);
            return true;
        }

        // Add to subscriptions map
        this.subscriptions.set(conversationId, {
            conversation_id: conversationId,
            callback,
            active: true
        });

        // Emit subscribe event to server - just conversation ID as string
        this.emit('subscribe', conversationId);

        console.log(`📡 Subscribed to conversation: ${conversationId}`);
        return true;
    }

    // Unsubscribe from conversation events
    unsubscribeFromConversation(conversationId: string): boolean {
        if (!conversationId) {
            console.error('❌ Invalid conversation ID for unsubscription');
            return false;
        }

        const subscription = this.subscriptions.get(conversationId);
        if (!subscription) {
            console.log(`📡 No active subscription found for conversation: ${conversationId}`);
            return false;
        }

        // Mark as inactive
        subscription.active = false;

        // Emit unsubscribe event to server - just conversation ID as string
        this.emit('unsubscribe', conversationId);

        // Remove from subscriptions map
        this.subscriptions.delete(conversationId);

        console.log(`📡 Unsubscribed from conversation: ${conversationId}`);
        return true;
    }

    // Get active subscriptions
    getActiveSubscriptions(): string[] {
        return Array.from(this.subscriptions.keys()).filter(id => {
            const sub = this.subscriptions.get(id);
            return sub?.active === true;
        });
    }

    // Check if subscribed to conversation
    isSubscribedToConversation(conversationId: string): boolean {
        const subscription = this.subscriptions.get(conversationId);
        return subscription?.active === true;
    }

    // Get subscription count
    getSubscriptionCount(): number {
        return this.subscriptions.size;
    }

    // Clear all subscriptions
    clearAllSubscriptions(): void {
        this.subscriptions.forEach((subscription, conversationId) => {
            this.unsubscribeFromConversation(conversationId);
        });
        this.subscriptions.clear();
        console.log('📡 Cleared all conversation subscriptions');
    }

    // Handle incoming events
    private handleIncomingEvent(eventName: string, data: any): void {
        try {
            // Add to event history
            this.addEventToHistory(eventName, data);

            // Process based on event type
            switch (eventName) {
                case 'token_usage':
                    this.handleTokenUsage(data);
                    break;
                case 'agent_message':
                    this.handleAgentMessage(data);
                    break;
                case 'processing_status':
                    this.handleProcessingStatus(data);
                    break;
                case 'streaming_section_response':
                    this.handleStreamingResponse(data);
                    break;
                case 'conversation_update':
                    this.handleConversationUpdate(data);
                    break;
                case 'email_generation':
                    this.handleEmailGeneration(data);
                    break;
                case 'suggestion_update':
                    this.handleSuggestionUpdate(data);
                    break;
                case 'executing_tool_call':
                    this.handleExecutingToolCall(data);
                    break;
                case 'tool_call_completed':
                    this.handleToolCallCompleted(data);
                    break;
                case 'subscribe_success':
                    this.handleSubscribeSuccess(data);
                    break;
                case 'subscribe_error':
                    this.handleSubscribeError(data);
                    break;
                case 'unsubscribe_success':
                    this.handleUnsubscribeSuccess(data);
                    break;
                case 'unsubscribe_error':
                    this.handleUnsubscribeError(data);
                    break;
                default:
                    console.log(`📡 Unhandled event: ${eventName}`, data);
            }
        } catch (error) {
            console.error(`❌ Error handling event '${eventName}':`, error);
        }
    }

    // Event handlers
    private handleTokenUsage(data: any): void {
        console.log('📊 Token usage update:', data);
        // Broadcast to all active subscriptions
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleAgentMessage(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleProcessingStatus(data: any): void {
        console.log('📊 Processing status update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message || {},
            processing_type: data.processing_type || 'processing',
            status: data.status || 'processing',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleStreamingResponse(data: any): void {
        console.log('📡 Streaming section response:', data);
        this.broadcastToSubscriptions(data);
    }

    private handleConversationUpdate(data: any): void {
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: data.message,
            suggestions: data.suggestions,
            next_action: data.next_action,
            is_input_restricted_to_suggestions: data.is_input_restricted_to_suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleEmailGeneration(data: any): void {
        console.log('📧 Email generation:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            email_content: data.email_content,
            status: data.status || 'success',
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSuggestionUpdate(data: any): void {
        console.log('💡 Suggestion update:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            suggestions: data.suggestions,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleExecutingToolCall(data: any): void {
        console.log('🔧 Executing tool call:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Executing tool: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'processing',
            status: 'processing',
            tool_name: data.tool_name,
            tool_parameters: data.tool_parameters,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleToolCallCompleted(data: any): void {
        console.log('✅ Tool call completed:', data);
        this.broadcastToSubscriptions({
            conversation_id: data.conversation_id || 'unknown',
            message: {
                role: 'system' as const,
                content: data.message?.content || `Tool completed: ${data.tool_name || 'Unknown tool'}`,
                timestamp: new Date().toISOString()
            },
            processing_type: 'completed',
            status: 'completed',
            tool_name: data.tool_name,
            tool_result: data.tool_result,
            execution_time: data.execution_time,
            timestamp: new Date().toISOString(),
            ...data
        });
    }

    private handleSubscribeSuccess(data: any): void {
        console.log('✅ Subscribe success:', data);
        const conversationId = data.conversation_id;
        if (conversationId && this.subscriptions.has(conversationId)) {
            const subscription = this.subscriptions.get(conversationId);
            if (subscription) {
                subscription.active = true;
                console.log(`📡 Successfully subscribed to conversation: ${conversationId}`);
            }
        }
    }

    private handleSubscribeError(data: any): void {
        console.error('❌ Subscribe error:', data);
        const conversationId = data.conversation_id;
        if (conversationId && this.subscriptions.has(conversationId)) {
            this.subscriptions.delete(conversationId);
            console.log(`📡 Removed failed subscription for conversation: ${conversationId}`);
        }
    }

    private handleUnsubscribeSuccess(data: any): void {
        console.log('✅ Unsubscribe success:', data);
    }

    private handleUnsubscribeError(data: any): void {
        console.error('❌ Unsubscribe error:', data);
    }

    // Broadcast event to all active subscriptions
    private broadcastToSubscriptions(eventData: MiaChatV2Event): void {
        const conversationId = eventData.conversation_id;
        
        if (conversationId && conversationId !== 'unknown') {
            // Send to specific conversation subscription
            const subscription = this.subscriptions.get(conversationId);
            if (subscription && subscription.active) {
                try {
                    subscription.callback(eventData);
                } catch (error) {
                    console.error(`❌ Error in subscription callback for ${conversationId}:`, error);
                }
            }
        } else {
            // Broadcast to all active subscriptions if no specific conversation ID
            this.subscriptions.forEach((subscription, id) => {
                if (subscription.active) {
                    try {
                        subscription.callback(eventData);
                    } catch (error) {
                        console.error(`❌ Error in subscription callback for ${id}:`, error);
                    }
                }
            });
        }
    }

    // Override connect to set up event listeners
    connect(): void {
        super.connect();
        
        // Set up event listeners for all MiaChatV2 events
        this.getEvents().forEach(eventName => {
            this.on(eventName, (data) => {
                this.handleIncomingEvent(eventName, data);
            });
        });
    }

    // Override disconnect to clean up subscriptions
    disconnect(): void {
        this.clearAllSubscriptions();
        super.disconnect();
    }

    // Get cached events for a conversation
    getCachedEvents(conversationId: string): MiaChatV2Event[] {
        return Array.from(this.eventCache.values())
            .filter(event => event.conversation_id === conversationId)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }

    // Clear cached events for a conversation
    clearCachedEvents(conversationId: string): void {
        const eventsToDelete: string[] = [];
        this.eventCache.forEach((event, key) => {
            if (event.conversation_id === conversationId) {
                eventsToDelete.push(key);
            }
        });
        eventsToDelete.forEach(key => this.eventCache.delete(key));
    }

    // Get service status
    getServiceStatus(): {
        connected: boolean;
        subscriptionCount: number;
        activeSubscriptions: string[];
        eventHistoryCount: number;
    } {
        return {
            connected: this.isConnected(),
            subscriptionCount: this.getSubscriptionCount(),
            activeSubscriptions: this.getActiveSubscriptions(),
            eventHistoryCount: this.getEventHistory().length
        };
    }
}

// Export singleton instance
export const miaChatV2SocketService = MiaChatV2SocketService.getInstance;

// Export the class for testing
export { MiaChatV2SocketService };

// Types are already exported above as interfaces
